import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { comprehensionService } from '../services/comprehension.service';
import type {
  ComprehensionQuestion,
  ComprehensionJobStatus,
  ComprehensionGeneratePayload,
} from '../types/api.types';

export interface UseComprehensionReturn {
  questions: ComprehensionQuestion[];
  pollingStatus: ComprehensionJobStatus | null;
  isPolling: boolean;
  isRateLimit: boolean;
  error: string | null;
  triggerGenerate: () => void;
}

/**
 * Manages the full lifecycle of AI comprehension question generation:
 *   trigger → (cache hit: done) | (miss: poll until complete) → questions
 *
 * Uses TanStack Query v5 patterns — data-derived state via useEffect
 * instead of deprecated onSuccess callback on useQuery.
 *
 * Accepts either videoId (YouTube) or episodeId (podcast). Exactly one must be
 * provided. Podcast episodes use the synthetic cache key "episode-{id}" which
 * the backend stores in video_id columns (TEXT, no FK).
 */
export function useComprehension(videoId: string | undefined, projectId: number, episodeId?: number): UseComprehensionReturn {
  // Stable cache key: YouTube UUID or "episode-{id}" for podcasts
  const cacheKey = videoId ?? (episodeId ? `episode-${episodeId}` : '');

  const [questions, setQuestions] = useState<ComprehensionQuestion[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingStatus, setPollingStatus] = useState<ComprehensionJobStatus | null>(null);
  const [isRateLimit, setIsRateLimit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Generation mutation ────────────────────────────────────────────────
  const { mutate: generate } = useMutation({
    mutationFn: (payload: ComprehensionGeneratePayload) =>
      comprehensionService.generate(payload),
    onSuccess: (data) => {
      if (data.questions && data.questions.length > 0) {
        // Cache hit — questions returned immediately
        setQuestions(data.questions);
        setPollingStatus('complete');
        setIsPolling(false);
      } else {
        // Job started — begin polling
        setPollingStatus(data.status ?? 'pending');
        setIsPolling(true);
      }
    },
    onError: (err: any) => {
      const status: number = err?.response?.status ?? 0;
      const msg: string = err?.response?.data?.error ?? '';
      if (status === 429 || msg.toLowerCase().includes('rate limit')) {
        setIsRateLimit(true);
      } else {
        setError(msg || 'Generation failed. Please try again.');
      }
      setIsPolling(false);
    },
  });

  // ── Status polling query ───────────────────────────────────────────────
  const { data: statusData } = useQuery({
    queryKey: ['comprehension', 'status', cacheKey],
    queryFn: () => comprehensionService.getStatus(cacheKey),
    enabled: isPolling && !!cacheKey,
    refetchInterval: isPolling ? 1500 : false,
    refetchOnWindowFocus: false,
  });

  // React to status changes (v5: useEffect instead of deprecated onSuccess)
  useEffect(() => {
    if (!statusData) return;

    setPollingStatus(statusData.status);

    if (statusData.status === 'complete') {
      setIsPolling(false);
      if (statusData.questions && statusData.questions.length > 0) {
        setQuestions(statusData.questions);
      } else {
        // Edge: complete but no questions in status — re-fetch via generate
        generate({ videoId, episodeId, projectId });
      }
    } else if (statusData.status === 'error') {
      setIsPolling(false);
      setError('Question generation failed. Please try again.');
    }
  }, [statusData, videoId, episodeId, projectId, generate]);

  // ── Public trigger ─────────────────────────────────────────────────────
  const triggerGenerate = useCallback(() => {
    setQuestions([]);
    setError(null);
    setIsRateLimit(false);
    setPollingStatus(null);
    setIsPolling(false);
    generate({ videoId, episodeId, projectId });
  }, [generate, videoId, episodeId, projectId]);

  return { questions, pollingStatus, isPolling, isRateLimit, error, triggerGenerate };
}
